import './dashboard-widgets'
import './locales/register'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import { RouteObject } from 'react-router-dom'

// Lazy load components for code splitting
import { lazy, Suspense } from 'react'

// Dashboard
const Dashboard = lazy(() => import('./components/Dashboard'))

// Schedule components
const ScheduleList = lazy(() => import('./components/Schedules/List'))
const ScheduleCreate = lazy(() => import('./components/Schedules/Create'))
const ScheduleUpdate = lazy(() => import('./components/Schedules/Update'))
const ScheduleDetail = lazy(() => import('./components/Schedules/Detail'))
const RotationEdit = lazy(() => import('./components/Schedules/RotationEdit'))

// Assignment components
const AssignmentList = lazy(() => import('./components/Assignments/List'))
const AssignmentDetail = lazy(() => import('./components/Assignments/Detail'))
/** @deprecated Unused — Dashboard has its own "Who's On-Call" section */
// const CurrentOncall = lazy(() => import('./components/Assignments/Current'))

// Swap components
const SwapList = lazy(() => import('./components/Swaps/List'))
const SwapCreate = lazy(() => import('./components/Swaps/Create'))
const SwapDetail = lazy(() => import('./components/Swaps/Detail'))

// Compensation components
const CompensationList = lazy(() => import('./components/Compensations/List'))
const CompensationReport = lazy(
    () => import('./components/Compensations/Report')
)
const CompensationDetail = lazy(
    () => import('./components/Compensations/Detail')
)

// Loading fallback
const LoadingFallback = () => (
    <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
)

// Wrapper for lazy components
const withSuspense = (
    Component: React.LazyExoticComponent<React.FC<any>>,
    props?: any
) => (
    <Suspense fallback={<LoadingFallback />}>
        <Component {...props} />
    </Suspense>
)

/**
 * Admin/HRM view - full management of on-call schedules
 * Path: /oncall/*
 */
export const OncallAdminModule: RouteObject = {
    path: 'oncall',
    children: [
        {
            path: '',
            element: withSuspense(Dashboard),
        },
        // Schedules
        {
            path: 'schedules',
            element: withSuspense(ScheduleList),
        },
        {
            path: 'schedules/new',
            element: withSuspense(ScheduleCreate),
        },
        {
            path: 'schedules/:id',
            element: withSuspense(ScheduleDetail),
        },
        {
            path: 'schedules/:id/edit',
            element: withSuspense(ScheduleUpdate),
        },
        {
            path: 'schedules/:id/rotations/:rotationId',
            element: withSuspense(RotationEdit),
        },
        // Assignments
        {
            path: 'assignments',
            element: withSuspense(AssignmentList),
        },
        {
            path: 'assignments/:id',
            element: withSuspense(AssignmentDetail),
        },
        // Swaps
        {
            path: 'swaps',
            element: withSuspense(SwapList, { pageType: 'admin' }),
        },
        {
            path: 'swaps/new',
            element: withSuspense(SwapCreate),
        },
        {
            path: 'swaps/:id',
            element: withSuspense(SwapDetail),
        },
        // Compensations
        {
            path: 'compensations',
            element: withSuspense(CompensationList),
        },
        {
            path: 'compensations/report',
            element: withSuspense(CompensationReport),
        },
        {
            path: 'compensations/:id',
            element: withSuspense(CompensationDetail),
        },
    ],
    errorElement: <ErrorPage status={500} />,
}
